import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { profileService } from "@/features/profile/services/visaService";
import { formatDisplayName } from "@/shared/utils/formatters";

interface UserOption {
  value: string;
  label: string;
}

interface UserSearchSelectProps {
  companyId: string;
  value: string;
  onChange: (userId: string, label: string) => void;
  label?: string;
  required?: boolean;
}

const MAX_RESULTS = 15;

export const UserSearchSelect = ({
  companyId,
  value,
  onChange,
  label,
  required,
}: UserSearchSelectProps) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserOption[]>([]);
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch users when companyId changes
  useEffect(() => {
    if (!companyId) {
      setUsers([]);
      setSearch("");
      onChange("", "");
      return;
    }
    setLoading(true);
    profileService
      .searchUsers({ company: companyId })
      .then((res) => {
        const list = Array.isArray(res) ? res : res.users || [];
        setUsers(
          list.map((u: any) => ({
            value: u.user_id,
            label: formatDisplayName(u.last_name, u.first_name, u.middle_name),
          }))
        );
      })
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [companyId]);

  // Sync display when value changes externally
  useEffect(() => {
    if (value) {
      const match = users.find((u) => u.value === value);
      if (match) setSearch(match.label);
    } else {
      setSearch("");
    }
  }, [value, users]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        if (value) {
          const match = users.find((u) => u.value === value);
          if (match) setSearch(match.label);
        } else {
          setSearch("");
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [value, users]);

  const filtered = useCallback(() => {
    const q = search.toLowerCase().trim();
    if (!q) return users.slice(0, MAX_RESULTS);
    return users
      .filter((u) => u.label.toLowerCase().includes(q))
      .slice(0, MAX_RESULTS);
  }, [search, users]);

  const results = filtered();

  const handleSelect = (user: UserOption) => {
    setSearch(user.label);
    setIsOpen(false);
    onChange(user.value, user.label);
  };

  const handleClear = () => {
    setSearch("");
    onChange("", "");
    inputRef.current?.focus();
  };

  return (
    <div ref={wrapperRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>

        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
            if (value) onChange("", "");
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={
            !companyId
              ? t("leave.user_select_company_first")
              : loading
              ? t("leave.user_loading")
              : t("leave.user_search_placeholder")
          }
          disabled={!companyId || loading}
          className="w-full pl-9 pr-9 py-2.5 md:py-2 border border-gray-300 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
        />

        {(search || value) && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !loading && companyId && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {results.length === 0 ? (
            <div className="px-3 py-4 text-sm text-gray-400 text-center">
              {t("leave.user_no_results")}
            </div>
          ) : (
            <>
              {results.map((u) => (
                <button
                  key={u.value}
                  type="button"
                  onClick={() => handleSelect(u)}
                  className={`w-full text-left px-3 py-2.5 md:py-2 text-sm hover:bg-indigo-50 transition-colors ${
                    u.value === value
                      ? "bg-indigo-50 text-indigo-700 font-medium"
                      : "text-gray-700"
                  }`}
                >
                  {u.label}
                </button>
              ))}
              {users.length > MAX_RESULTS && search.trim() === "" && (
                <div className="px-3 py-2 text-xs text-gray-400 text-center border-t border-gray-100">
                  {t("leave.user_type_to_filter")}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
